import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ChevronLeft, 
  Star, 
  Loader2, 
  CheckCircle, 
  ThumbsUp,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { usePublicData } from "@/hooks/usePublicData";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { SITE_ORIGIN } from "@/lib/publicSite";

const FACEBOOK_URL = "https://www.facebook.com/share/14SYLdTSJVp/";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25pknzhh_ee436b9cf26fbe3a949749993a8a0799c33dc4d497d0bf4b65f923b400bf1fc0.jpeg";
const API = `${window.location.origin}/api`;

const services = [
  { value: "carpentry", label: "Carpentry" },
  { value: "general-repairs", label: "General Repairs" },
  { value: "furniture-builds", label: "Furniture Builds" }
];

// Accessible star rating — radiogroup with unique names per control, roving
// tabindex, arrow-key operation, visible focus ring and a non-colour "n of 5"
// selection indicator.
const StarRating = ({ value, onChange, label, groupId }) => {
  const [hover, setHover] = useState(0);
  const labelId = `${groupId}-label`;
  const plainLabel = label.replace("*", "").trim();

  const handleKeyDown = (e) => {
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") next = Math.min(5, (value || 0) + 1);
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = Math.max(1, (value || 1) - 1);
    else if (e.key === "Home") next = 1;
    else if (e.key === "End") next = 5;
    if (next !== null) {
      e.preventDefault();
      onChange(next);
      e.currentTarget.querySelectorAll("button")[next - 1]?.focus();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label id={labelId}>{label}</Label>
        <span className="text-sm text-muted-foreground" data-testid={`${groupId}-value`} aria-hidden="true">
          {value ? `${value} of 5` : "Not rated"}
        </span>
      </div>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="flex gap-1"
        onKeyDown={handleKeyDown}
        data-testid={groupId}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${plainLabel}: ${star} star${star > 1 ? "s" : ""}`}
            tabIndex={star === (value || 1) ? 0 : -1}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 rounded transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            data-testid={`${groupId}-star-${star}`}
          >
            <Star 
              className={`h-8 w-8 transition-colors ${
                star <= (hover || value) 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : 'text-gray-300'
              }`}
              aria-hidden="true"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default function FeedbackPage() {
  const { bizInfo, services: publicServices } = usePublicData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useSeoHeadTags({
    title: "Leave Feedback | Sustainable Handyman Services | Abergavenny",
    description: "Share your experience with Sustainable Handyman Services. Rate our carpentry, repairs and furniture work in Abergavenny & Monmouthshire.",
    canonical: `${SITE_ORIGIN}/feedback`,
    ogType: "website",
  });

  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    service_type: "",
    overall_rating: 0,
    quality_rating: 0,
    communication_rating: 0,
    timeliness_rating: 0,
    recommend: true,
    comments: "",
    improvement_suggestions: "",
    can_use_as_testimonial: false
  });

  // Track page view
  useEffect(() => {
    const trackPageView = async () => {
      try {
        let sessionId = sessionStorage.getItem('shs_session_id');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('shs_session_id', sessionId);
        }
        await axios.post(`${API}/analytics/pageview`, null, {
          params: { page: '/feedback', session_id: sessionId }
        });
      } catch (error) { console.error('Failed to fetch feedback:', error); }
    };
    trackPageView();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.service_type || !formData.overall_rating) {
      toast.error("Please fill in your name, service type, and overall rating");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, formData);
      setIsSuccess(true);
      toast.success("Thank you for your feedback!");
    } catch (error) {
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold font-heading mb-2">Thank You!</h1>
            <p className="text-muted-foreground mb-6">
              Your feedback helps us improve our services and serve you better.
            </p>
            <div className="space-y-3">
              <Link to="/">
                <Button className="w-full">Back to Home</Button>
              </Link>
              <a 
                href={FACEBOOK_URL}
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="w-full">
                  <ThumbsUp className="h-4 w-4 mr-2" />
                  Leave a Facebook Review
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav />

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>

          <Card>
            <CardHeader className="text-center">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-semibold leading-none tracking-tight font-heading">Share Your Experience</h1>
              <CardDescription>
                We value your feedback! Please take a moment to let us know how we did.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name *</Label>
                    <Input 
                      id="name"
                      placeholder="John Smith"
                      value={formData.customer_name}
                      onChange={(e) => setFormData(f => ({ ...f, customer_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email (Optional)</Label>
                    <Input 
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Service Type */}
                <div className="space-y-2">
                  <Label htmlFor="feedback-service-type">Service Type *</Label>
                  <Select 
                    value={formData.service_type} 
                    onValueChange={(v) => setFormData(f => ({ ...f, service_type: v }))}
                  >
                    <SelectTrigger id="feedback-service-type" data-testid="feedback-service-select">
                      <SelectValue placeholder="Select the service you received" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Ratings */}
                <div className="space-y-6 p-4 bg-secondary/30 rounded-lg">
                  <h3 className="font-semibold">Rate Your Experience</h3>
                  
                  <StarRating 
                    label="Overall Experience *"
                    groupId="rating-overall"
                    value={formData.overall_rating}
                    onChange={(v) => setFormData(f => ({ ...f, overall_rating: v }))}
                  />
                  
                  <StarRating 
                    label="Quality of Work"
                    groupId="rating-quality"
                    value={formData.quality_rating}
                    onChange={(v) => setFormData(f => ({ ...f, quality_rating: v }))}
                  />
                  
                  <StarRating 
                    label="Communication"
                    groupId="rating-communication"
                    value={formData.communication_rating}
                    onChange={(v) => setFormData(f => ({ ...f, communication_rating: v }))}
                  />
                  
                  <StarRating 
                    label="Timeliness"
                    groupId="rating-timeliness"
                    value={formData.timeliness_rating}
                    onChange={(v) => setFormData(f => ({ ...f, timeliness_rating: v }))}
                  />
                </div>

                {/* Would Recommend */}
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <Checkbox 
                    id="recommend"
                    checked={formData.recommend}
                    onCheckedChange={(checked) => setFormData(f => ({ ...f, recommend: checked }))}
                  />
                  <Label htmlFor="recommend" className="text-sm cursor-pointer">
                    I would recommend Sustainable Handyman Services to friends and family
                  </Label>
                </div>

                {/* Comments */}
                <div className="space-y-2">
                  <Label htmlFor="comments">Comments</Label>
                  <Textarea 
                    id="comments"
                    placeholder="Tell us about your experience..."
                    rows={4}
                    value={formData.comments}
                    onChange={(e) => setFormData(f => ({ ...f, comments: e.target.value }))}
                  />
                </div>

                {/* Improvement Suggestions */}
                <div className="space-y-2">
                  <Label htmlFor="suggestions">Any suggestions for improvement?</Label>
                  <Textarea 
                    id="suggestions"
                    placeholder="We&apos;re always looking to improve..."
                    rows={3}
                    value={formData.improvement_suggestions}
                    onChange={(e) => setFormData(f => ({ ...f, improvement_suggestions: e.target.value }))}
                  />
                </div>

                {/* Testimonial Permission */}
                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox 
                    id="testimonial"
                    checked={formData.can_use_as_testimonial}
                    onCheckedChange={(checked) => setFormData(f => ({ ...f, can_use_as_testimonial: checked }))}
                  />
                  <Label htmlFor="testimonial" className="text-sm cursor-pointer">
                    You may use my feedback as a testimonial on your website (your name will be displayed)
                  </Label>
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Feedback"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <PublicFooter bizInfo={bizInfo} services={publicServices} />
    </div>
  );
}
